import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import { AccommodationPackageOptionTileVisualStyle } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'

import PropertyPromotionDetails from './PropertyPromotionDetails'

export const AnytimeRateOptionTile = styled.div`
  ${AccommodationPackageOptionTileVisualStyle}

  display: grid;
  gap: ${rem(12)} ${rem(32)};
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)} ${rem(32)};
    grid-template-columns: ${rem(238)} 1fr;
  }
`

function isValidInclusion({ minDuration, maxDuration }: App.OfferInclusion, duration: number): boolean {
  return (
    (minDuration == null || duration >= minDuration) &&
    (maxDuration == null || duration <= maxDuration)
  )
}

interface Props {
  propertyRoom: App.Property.Room
  duration: number
}

function PropertyAnytimeRateOption(props: Props) {
  const { propertyRoom, duration } = props

  const intl = useIntl()
  const [promotion, inclusions, bonusInclusions, luxPlusInclusions] = useMemo(() => {
    const { promotion, inclusions, bonusInclusions, luxPlusInclusions } = propertyRoom
    return [
      promotion && duration >= promotion.minimumLos ? promotion : undefined,
      inclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
      bonusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
      luxPlusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
    ]
  }, [propertyRoom, duration])

  if (!promotion && inclusions.length === 0 && bonusInclusions.length === 0 && luxPlusInclusions.length === 0) return null

  return <AnytimeRateOptionTile>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {promotion && <LabelGroup>
          <ProductTypeLabel productType="limited_time_special" />
        </LabelGroup>}
        <BodyText
          variant="large"
          weight="bold"
          colour="neutral-one"
        >
          {intl.formatMessage({
            id: 'Q81wyQpQgKNK8UxtYyU6',
            defaultMessage: 'Select dates to unlock package options',
            description: 'Placeholder text when dates aren’t selected for property anytime rate option',
          })}
        </BodyText>
      </VerticalSpacer>
      {promotion && <PropertyPromotionDetails promotion={promotion} />}
    </VerticalSpacer>
    <div>
      <LargeContentToggle
        maxHeight={208}
        showMoreText={intl.formatMessage({
          id: 'HYsmEAZvwGf6aOMdJgYl',
          defaultMessage: 'View more inclusions',
          description: 'Toggle text to expand inclusions list for property anytime rate option',
        })}
        showLessText={intl.formatMessage({
          id: '3nlGejct7MEq8uv6MIUa',
          defaultMessage: 'View less inclusions',
          description: 'Toggle text to collapse inclusions list for property anytime rate option',
        })}
      >
        <VerticalSpacer gap={12}>
          {luxPlusInclusions.length > 0 && <LuxPlusInclusions
            luxPlusInclusions={luxPlusInclusions}
          />}
          {bonusInclusions.length > 0 && <OfferPagePackageInclusionsList variant="bonus">
            {bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
          </OfferPagePackageInclusionsList>}
          {inclusions.length > 0 && <VerticalSpacer gap={4}>
            {inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
          </VerticalSpacer>}
        </VerticalSpacer>
      </LargeContentToggle>
    </div>
  </AnytimeRateOptionTile>
}

export default withOfferPageState((state) => ({
  duration: state.duration,
  open: !!state.activeFilter,
}), PropertyAnytimeRateOption, preventRenderWithOpenFilter)
